/*
 * Copyright © 2026, Commonwealth Scientific and Industrial Research Organisation
 * (CSIRO) ABN 41 687 119 230. All rights reserved
 *
 * Author: Derek Ireland
 */
import { PATIENT_READ } from "./const";

/** The event key of the archived tab in the patient list. */
export const ARCHIVED_TAB_KEY = "archived";

/**
 * Builds the SWR key for a patient list request. The API path ends in the
 * `isActive` flag, so the active list is `/patient/read/true` and the archived
 * list is `/patient/read/false`. A null key tells SWR not to fetch, which is how
 * the archived list is held back until the clinician first opens its tab.
 *
 * @param active - True for the active list, false for the archived list.
 * @param enabled - Whether the request should be made at all.
 * @returns The request path, or null when the request should not be made.
 * @example
 * patientListKey(false, false); // null, archived tab not yet opened
 * patientListKey(false, true);  // "/patient/read/false"
 */
export const patientListKey = (active: boolean, enabled: boolean): string | null =>
  enabled ? `${PATIENT_READ}${active}` : null;

/**
 * Tracks whether the archived tab has ever been opened this session. Once it
 * has, the archived list stays subscribed so a later archive or unarchive can
 * refresh it, so the flag only ever moves from false to true.
 *
 * @param previouslyOpened - Whether the tab had already been opened.
 * @param selectedTab - The event key of the tab just selected, or null when none.
 * @returns True once the archived tab has been selected at least once.
 */
export const hasOpenedArchivedTab = (previouslyOpened: boolean, selectedTab: string | null): boolean =>
  previouslyOpened || selectedTab === ARCHIVED_TAB_KEY;
